import { useCallback, useEffect, useState } from 'react';
import { Formik, Form, FormikProps, FormikHelpers } from 'formik';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import * as Yup from 'yup';
import { useAuth } from '../../hooks/useAuth';
import { Button, Alert } from '../../components/primitives';
import { FormInput } from '../../components/form';
import { AuthLayout } from '../AuthLayout';
import { ResultCard } from '../../components/ResultCard';
import { MailCheck } from 'lucide-react';

export default function ForgotPassword() {
  const navigate = useNavigate();
  const auth = useAuth();

  interface ForgotPasswordValues {
    email: string;
  }

  const [success, setSuccess] = useState<boolean>(false);
  const [sentToEmail, setSentToEmail] = useState<string>('');
  const [submitError, setSubmitError] = useState<string | undefined>(undefined);

  // Deferred navigation lives in an effect so it is cleanup-safe and survives
  // provider re-renders.
  useEffect(() => {
    if (!success) return;
    const timer = setTimeout(() => {
      navigate('/reset-password?email=' + encodeURIComponent(sentToEmail));
    }, 2000);
    return () => clearTimeout(timer);
  }, [success, sentToEmail, navigate]);

  const forgotPasswordValidation = Yup.object().shape({
    email: Yup.string()
      .email('A valid email address is required.')
      .required('An email address is required.'),
  });

  const handleOnSubmit = useCallback(
    (
      values: ForgotPasswordValues,
      actions: FormikHelpers<ForgotPasswordValues>
    ) => {
      setSubmitError(undefined);

      auth &&
        auth
          .forgotPassword(values.email)
          .then(() => {
            setSentToEmail(values.email);
            setSuccess(true);
          })
          .catch((err) => {
            actions.setSubmitting(false);
            setSubmitError(err.message);
          });
    },
    [auth, navigate]
  );

  const handleOnChange = () => {
    setSubmitError(undefined);
  };

  if (success) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-surface px-4 py-12">
        <ResultCard
          tone="info"
          icon={MailCheck}
          title="Password verification sent."
          description="We've sent a password reset code to your inbox."
          testId="reset-code-sent"
        />
      </div>
    );
  }

  return (
    <AuthLayout
      title="Forgot your password?"
      subtitle="Enter your email address to recieve a password reset code in your inbox."
      banner={
        submitError && (
          <Alert variant="destructive">
            {submitError}
          </Alert>
        )
      }
    >
      <Formik
        initialValues={{
          email: '',
        }}
        onSubmit={handleOnSubmit}
        validationSchema={forgotPasswordValidation}
      >
        {(props: FormikProps<ForgotPasswordValues>) => (
          <Form className="space-y-5" onChange={handleOnChange}>
            <FormInput
              label="Email"
              id="email"
              name="email"
              required
              disabled={props.isSubmitting}
            />
            <Button type="submit" className="w-full" disabled={props.isSubmitting}>
              {props.isSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
              Send
            </Button>
          </Form>
        )}
      </Formik>
    </AuthLayout>
  );
}
